import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { CustomButton } from '@medtronic/pecc-react-component-library-js';
import { FormControlLabel, MenuItem, Select } from '@mui/material';
import { RoleEnum } from '~/api/types';
import {
  useAddMemberMutation,
  useGetProjectQuery,
  useRemoveMemberMutation,
  useTransferOwnershipMutation,
} from '~/api/v2/hooks';
import { useAppDispatch, useAppSelector } from '~/redux';
import { selectCurrentUserApplicationRole, selectCurrentUserId } from '~/redux/slices/currentUserSlice';
import { ModalTypesEnum, openModal } from '~/redux/slices/modalSlice';

const ChangeProjectOwnerForm = () => {
  const dispatch = useAppDispatch();
  const projectId = +useParams().projectid!;
  const userId = useAppSelector(selectCurrentUserId);
  const userApplicationRole = useAppSelector(selectCurrentUserApplicationRole);
  const { data: projectData, isFetching } = useGetProjectQuery({ projectId });
  const projectOwnerId = projectData?.members.find((member) => member.role_id === RoleEnum.OWNER)?.id;
  const [, { isLoading: isRemovingMember }] = useRemoveMemberMutation({
    fixedCacheKey: `remove-member-${projectId}`,
  });
  const [, { isLoading: isAddingMember }] = useAddMemberMutation({
    fixedCacheKey: `add-member-${projectId}`,
  });
  const [transferOwnership, { isLoading: isTransferingOwnership }] = useTransferOwnershipMutation({
    fixedCacheKey: `transfer-ownership-${projectId}`,
  });

  const isLoading = isFetching || isTransferingOwnership || isRemovingMember || isAddingMember;

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      ownerId: '',
    },
  });

  const ownerId = watch('ownerId');
  const selectedUser = projectData?.members?.find(({ id }) => id === ownerId);

  const eligibleMembers = projectData?.members?.filter(
    ({ id }) =>
      (id !== userId ||
        (userApplicationRole && [RoleEnum.ADMINISTRATOR, RoleEnum.DEVELOPER].includes(userApplicationRole))) &&
      id !== projectOwnerId,
  );

  const onSubmit = async (data: { ownerId: string }) => {
    reset();

    await transferOwnership({
      projectId,
      user_id: data.ownerId,
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ flex: 1 }}>
      <FormControlLabel
        control={
          <Controller
            name="ownerId"
            control={control}
            rules={{ required: true }}
            disabled={!eligibleMembers?.length || isLoading}
            render={({ field }) => (
              <Select {...field}>
                {eligibleMembers?.map(({ id, first_name, last_name }) => (
                  <MenuItem key={id} value={id}>
                    {`${first_name} ${last_name}`}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        }
        label="CHANGE PROJECT OWNER"
      />
      {isDirty ? (
        <CustomButton
          onClick={() => {
            dispatch(
              openModal({
                type: ModalTypesEnum.CONFIRM,
                payload: {
                  onSubmit: handleSubmit(onSubmit),
                  title: `Change the project owner for this project to ${selectedUser?.first_name} ${selectedUser?.last_name}?`,
                  description: `Are you sure you want to change owner for ${projectData?.name}? This will reassign ownership to ${selectedUser?.first_name} ${selectedUser?.last_name} and you will no longer have ownership of the project.`,
                },
              }),
            );
          }}
          disabled={isLoading}
          label="Submit"
          style={{ marginTop: '2rem', textTransform: 'capitalize' }}
        />
      ) : null}
    </form>
  );
};

export default ChangeProjectOwnerForm;
